import React, { useState, useEffect, useMemo } from 'react';
import {
    MdStar, MdImage, MdPerson, MdWarning, MdCheckCircle,
    MdClose, MdKeyboardArrowLeft, MdKeyboardArrowRight, MdZoomOutMap
} from 'react-icons/md';

import { api } from 'services/api';
import { API_BASE_URL } from 'config';

// --- YARDIMCI FONKSİYON ---
const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
    const imagePath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}${imagePath}`;
};

// --- SKELETON LOADER ---
const GallerySkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
        {[...Array(6)].map((_, i) => (
            <div key={i} className="flex flex-col h-[400px] w-full rounded-[20px] bg-white dark:bg-navy-800 shadow-sm border border-gray-100 dark:border-white/10 overflow-hidden">
                <div className="h-[250px] w-full bg-gray-200 dark:bg-navy-700" />
                <div className="p-4 flex-1 flex flex-col justify-between">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-navy-700" />
                        <div className="h-4 w-32 bg-gray-200 dark:bg-navy-700 rounded" />
                    </div>
                    <div className="h-10 w-full bg-gray-200 dark:bg-navy-700 rounded-xl" />
                </div>
            </div>
        ))}
    </div>
);

// --- BİLEŞENLER ---

// Sadece Okunabilir (Ortalama) Yıldızlar
const ReadOnlyStars = ({ rating }) => {
    const rounded = Math.round(Number(rating) || 0);
    return (
        <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
                <MdStar
                    key={star}
                    className={`text-sm ${star <= rounded ? 'text-amber-400 drop-shadow-sm' : 'text-gray-300 dark:text-gray-600'}`}
                />
            ))}
        </div>
    );
};

// Etkileşimli (Kullanıcı Oylaması) Yıldızlar
const InteractiveStars = ({ currentRating, onRate, isLoading }) => {
    const [hoverVal, setHoverVal] = useState(0);

    return (
        <div className="flex items-center gap-1" onMouseLeave={() => setHoverVal(0)}>
            {[1, 2, 3, 4, 5].map((star) => (
                <MdStar
                    key={star}
                    className={`text-2xl transition-all duration-200 ${
                        (hoverVal || currentRating) >= star ? 'text-amber-400 drop-shadow-md scale-110' : 'text-gray-300 dark:text-gray-600'
                    } ${isLoading ? 'cursor-wait opacity-50' : 'cursor-pointer hover:scale-125'}`}
                    onMouseEnter={() => !isLoading && setHoverVal(star)}
                    onClick={() => !isLoading && onRate(star)}
                />
            ))}
            {isLoading && (
                <div className="ml-2 h-4 w-4 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
            )}
        </div>
    );
};

// --- ANA COMPONENT ---
const CustomerGallery = () => {
    // --- STATELER (WORK-LOGIC BİREBİR) ---
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [submittingId, setSubmittingId] = useState(null); // Hangi karta puan veriliyor

    // HorizonUI Extra: Lightbox (Görsel Büyütme) ve Sayfalama
    const [lightboxImage, setLightboxImage] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 9;

    // --- API İSTEKLERİ ---
    const fetchGallery = async () => {
        setLoading(true);
        try {
            const response = await api.get('/gallery');
            if (response.success || response.images) {
                // En yeniler en üstte
                const rawData = response.images || response.data || [];
                const sorted = [...rawData].sort((a, b) => b.id - a.id);
                setImages(sorted);
            } else {
                setError('Galeri verileri alınamadı.');
            }
        } catch (err) {
            console.error('Galeri yükleme hatası:', err);
            setError('Bağlantı hatası oluştu.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGallery();
    }, []);

    const handleRatingChange = async (imageId, newValue) => {
        if (!newValue) return;
        setSubmittingId(imageId);

        try {
            const response = await api.post(`/gallery/${imageId}/rate`, {
                rating: newValue
            });

            if (response.success || response.average_rating !== undefined) {
                // State'i anında güncelle (Work-Logic korundu)
                setImages((prevImages) =>
                    prevImages.map((img) =>
                        img.id === imageId
                            ? {
                                ...img,
                                my_rating: newValue,
                                average_rating: response.average_rating || img.average_rating,
                                rating_count: img.my_rating ? img.rating_count : (img.rating_count || 0) + 1
                            }
                            : img
                    )
                );
            }
        } catch (err) {
            alert('Puan verilirken bir hata oluştu.');
            console.error(err);
        } finally {
            setSubmittingId(null);
        }
    };

    // --- SAYFALAMA HESAPLARI ---
    const totalPages = Math.max(1, Math.ceil(images.length / PAGE_SIZE));
    const currentImages = images.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    // --- RENDER ---
    return (
        <div className="mt-5 flex w-full flex-col gap-6 pb-20 animate-fadeIn">

            {/* 1. VİTRİN KARTI (HERO HEADER) */}
            <div className="relative flex flex-col justify-center rounded-[20px] bg-gradient-to-br from-brand-500 to-brand-600 dark:from-navy-800 dark:to-navy-900 p-8 md:p-10 shadow-lg shadow-brand-500/20 overflow-hidden min-h-[220px]">
                {/* Arka plan ikonu */}
                <div className="absolute -top-10 -right-10 opacity-10 rotate-12">
                    <MdImage className="text-[250px] text-white" />
                </div>

                <div className="relative z-10 flex items-center gap-6">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-md border border-white/20 shadow-inner flex-none">
                        <MdStar className="text-4xl text-white" />
                    </div>
                    <div>
                        <h2 className="text-3xl md:text-4xl font-black text-white mb-2 drop-shadow-md">İşlem Galerisi</h2>
                        <p className="text-base font-medium text-white/80 max-w-xl">
                            Uzmanlarımızın yaptığı işlemleri inceleyin, ilham alın ve değerlendirin. Tarzınızı yansıtacak detayları keşfedin.
                        </p>
                    </div>
                </div>
            </div>

            {error && (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-red-50 text-red-600 border border-red-200 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400 text-sm font-bold shadow-sm">
                    <MdWarning className="text-2xl flex-none" /> {error}
                </div>
            )}

            {/* 2. GALERİ GRID */}
            {loading ? (
                <GallerySkeleton />
            ) : images.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10">
                    <MdImage className="text-7xl text-gray-300 dark:text-gray-600 mb-4 opacity-50" />
                    <h4 className="text-xl font-bold text-navy-700 dark:text-white">Henüz görsel yüklenmemiş.</h4>
                    <p className="text-sm font-medium text-gray-500 mt-2">Çok yakında harika tasarımlarla buradayız!</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {currentImages.map((img) => {
                        const imgSrc = getImageUrl(img.image_url || img.image_path);

                        return (
                            <div key={img.id} className="group flex flex-col bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/5 overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-300">

                                {/* GÖRSEL ALANI */}
                                <div className="relative h-64 w-full bg-gray-100 dark:bg-navy-900 overflow-hidden cursor-pointer" onClick={() => setLightboxImage(imgSrc)}>
                                    <img
                                        src={imgSrc}
                                        alt={img.description || 'Galeri Resmi'}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = 'https://via.placeholder.com/400x300?text=Resim+Bulunamadi';
                                        }}
                                    />

                                    {/* Büyüteç Overlay */}
                                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-[2px]">
                                        <div className="h-12 w-12 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 text-white">
                                            <MdZoomOutMap className="text-2xl drop-shadow-md" />
                                        </div>
                                    </div>

                                    {/* Ortalama Puan Rozeti */}
                                    <div className="absolute top-3 right-3 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md border border-white/10 d-flex items-center gap-1 shadow-md">
                                        <span className="text-sm font-black text-white mr-1">{parseFloat(img.average_rating || 0).toFixed(1)}</span>
                                        <MdStar className="text-amber-400 text-sm" />
                                    </div>
                                </div>

                                {/* DETAY VE PUANLAMA ALANI */}
                                <div className="flex-1 flex flex-col p-5">

                                    {/* Personel Bilgisi */}
                                    <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100 dark:border-white/10">
                                        {img.staff ? (
                                            <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-brand-500 text-white font-bold shadow-sm">
                                                {img.staff.name.charAt(0)}{img.staff.surname?.charAt(0)}
                                            </div>
                                        ) : (
                                            <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-gray-200 dark:bg-navy-700 text-gray-500 dark:text-gray-400 shadow-sm">
                                                <MdPerson className="text-xl" />
                                            </div>
                                        )}
                                        <div>
                                            <h6 className="text-sm font-bold text-navy-700 dark:text-white leading-tight">
                                                {img.staff ? `${img.staff.name} ${img.staff.surname}` : 'Genel Çalışma'}
                                            </h6>
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">
                                                {img.staff ? 'Uzman Personel' : 'İşletme Galerisi'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Açıklama */}
                                    {img.description && (
                                        <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-4 line-clamp-3 italic">
                                            "{img.description}"
                                        </p>
                                    )}

                                    <div className="mt-auto pt-2">
                                        {/* Oy Bilgileri */}
                                        <div className="flex items-center justify-between mb-4 px-4 py-2 bg-gray-50 dark:bg-navy-900 rounded-xl border border-gray-100 dark:border-white/5">
                                            <div className="flex flex-col">
                                                <span className="text-[10px] font-bold text-gray-400 uppercase">Genel Puan</span>
                                                <ReadOnlyStars rating={img.average_rating} />
                                            </div>
                                            <div className="text-right flex flex-col">
                                                <span className="text-[10px] font-bold text-gray-400 uppercase">Oy Sayısı</span>
                                                <span className="text-sm font-black text-navy-700 dark:text-white">{img.rating_count || 0}</span>
                                            </div>
                                        </div>

                                        {/* Kullanıcı Puanlama Aksiyonu */}
                                        <div className="flex flex-col items-center p-4 bg-brand-50 dark:bg-brand-400/10 rounded-xl border border-brand-100 dark:border-brand-400/20">
                                            {img.my_rating ? (
                                                <div className="flex items-center gap-2 text-brand-600 dark:text-brand-400 font-bold text-sm">
                                                    <MdCheckCircle className="text-xl" /> Senin Puanın: {img.my_rating}
                                                </div>
                                            ) : (
                                                <>
                                                    <span className="text-xs font-bold text-brand-700 dark:text-brand-300 mb-2">Bu işlemi nasıl buldun?</span>
                                                    <InteractiveStars
                                                        currentRating={0}
                                                        onRate={(val) => handleRatingChange(img.id, val)}
                                                        isLoading={submittingId === img.id}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </div>

                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* 3. SAYFALAMA (PAGINATION) */}
            {!loading && totalPages > 1 && (
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100 dark:border-white/10 bg-white dark:bg-navy-800 p-4 rounded-[20px] shadow-sm">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        <span className="font-bold text-navy-700 dark:text-white">{images.length}</span> görselden <span className="font-bold text-navy-700 dark:text-white">{(currentPage-1)*PAGE_SIZE + 1}-{Math.min(currentPage*PAGE_SIZE, images.length)}</span> gösteriliyor.
                    </p>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all active:scale-95"
                        >
                            <MdKeyboardArrowLeft className="text-2xl" />
                        </button>
                        <div className="flex h-10 min-w-[40px] items-center justify-center rounded-xl bg-brand-500 text-white text-sm font-bold shadow-md px-3">
                            {currentPage}
                        </div>
                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all active:scale-95"
                        >
                            <MdKeyboardArrowRight className="text-2xl" />
                        </button>
                    </div>
                </div>
            )}

            {/* 4. GLASSY LIGHTBOX (GÖRSEL BÜYÜTME) */}
            {lightboxImage && (
                <div
                    className="fixed inset-0 z-[200] flex items-center justify-center bg-navy-900/80 backdrop-blur-xl p-4 md:p-10 transition-all animate-fadeIn cursor-pointer"
                    onClick={() => setLightboxImage(null)}
                >
                    {/* Kapat Butonu */}
                    <button
                        className="absolute top-6 right-6 h-12 w-12 flex items-center justify-center rounded-full bg-white/10 border border-white/20 text-white hover:bg-red-500 hover:border-red-500 transition-all z-[201]"
                        onClick={(e) => { e.stopPropagation(); setLightboxImage(null); }}
                    >
                        <MdClose className="text-2xl" />
                    </button>

                    {/* Glassy Çerçeveli Resim */}
                    <div
                        className="relative max-h-[90vh] max-w-[90vw] md:max-w-[80vw] rounded-3xl p-2 bg-white/5 border border-white/20 shadow-2xl"
                        onClick={(e) => e.stopPropagation()} // Resme tıklanınca kapanmayı engeller
                    >
                        <img
                            src={lightboxImage}
                            alt="Büyük Önizleme"
                            className="max-h-[85vh] w-auto object-contain rounded-2xl shadow-inner"
                        />
                    </div>
                </div>
            )}

        </div>
    );
};

export default CustomerGallery;